/**
 * RollSettingsDrawer - Slide-out drawer for Roll page settings.
 * Surfaces the capture-mode toggle (photo vs video) as a discoverable
 * alternative to the long-press gesture on the FloatingCaptureButton.
 * Mirrors PracticeSettingsDrawer's structure (landscape/portrait variants).
 * @module RollSettingsDrawer
 */

import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { CaptureModeToggle, type CaptureMode } from '@/components/CaptureModeToggle';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { cn } from '@/lib/utils';

/**
 * Props for RollSettingsDrawer component.
 */
export interface RollSettingsDrawerProps {
  /** Whether drawer is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Currently selected capture mode (from persistent app settings) */
  captureMode: CaptureMode;
  /** Callback when the user changes capture mode */
  onCaptureModeChange: (mode: CaptureMode) => void;
  /**
   * Optional GM-imposed lock. When set, the toggle is shown as the locked value
   * (overriding `captureMode` visually) and is disabled. Derived by RollPage from
   * the active roll request's `proofMode`:
   *   'image' -> 'photo' lock, 'video' -> 'video' lock, otherwise undefined.
   */
  lockedMode?: CaptureMode;
}

/**
 * Slide-out drawer for Roll page settings.
 * Slides from bottom in portrait, right in landscape.
 */
export function RollSettingsDrawer({
  open,
  onOpenChange,
  captureMode,
  onCaptureModeChange,
  lockedMode
}: RollSettingsDrawerProps) {
  const isLandscape = useIsLandscape();
  const effectiveValue: CaptureMode = lockedMode ?? captureMode;
  const lockReason =
    lockedMode === 'photo'
      ? 'GM requires a photo'
      : lockedMode === 'video'
      ? 'GM requires a video'
      : undefined;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={isLandscape ? 'right' : 'bottom'}>
      <DrawerContent
        direction={isLandscape ? 'right' : 'bottom'}
        className={cn(isLandscape ? 'w-72' : 'max-h-[60vh]')}
      >
        <DrawerHeader>
          <DrawerTitle className="text-glow-subtle">Roll Settings</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Capture mode toggle */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Capture Mode</label>
            <CaptureModeToggle
              value={effectiveValue}
              onChange={onCaptureModeChange}
              disabled={lockedMode !== undefined}
              lockReason={lockReason}
            />
            {!lockedMode && (
              <p className="text-xs text-muted-foreground">
                {captureMode === 'video'
                  ? 'Tap to record a short clip of the roll'
                  : 'Tap to capture a single photo of the dice'}
              </p>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
